import React, { useEffect, useState } from "react";

import { useIntl } from "react-intl";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";

import { Trial } from "Containers/Banner";
import Loading from "ds/PageLoading";
import { WizardStepperWrapper, RoutableWizardStep } from "ds/WizardStepper";
import {
  organizationLoadingSelector,
  organizationsSelector,
  organizationByDescriptionIdSelector
} from "Reducers/organization";
import useProvisioningPuller from "Reducers/organization/projects/hooks/useProvisioningPuller";
import Logo from "src/common/containers/NavBar/Logo/Logo";
import { useAppSelector } from "Store/hooks";

import { SetupStepperContextProvider, useSetupStepperContext } from "./Context";
import * as S from "./Routes.styles";
import { onboardingTypes, verificationTypes } from "./types";

const OnboardingType = React.lazy(
  () => import("./pages/onboardingType/OnboardingType")
);
const Details = React.lazy(() => import("./pages/details/Details"));
const Verify = React.lazy(() => import("./pages/verify/Verify"));
const Setup = React.lazy(() => import("./pages/setup/Setup"));
const Repository = React.lazy(() => import("./pages/repository/Repository"));

const redirectSubdirectories = ["details", "verify"];

const cancellableSubdirectories = [
  "details",
  "verify",
  "migrate",
  "kickstart",
  "repository",
  "setup",
  "create-project"
];

const stepperSubdirectories = [
  "details",
  "verify",
  "migrate",
  "kickstart",
  "repository",
  "setup"
];

const onboardingTypeNumberOfStepsMap = {
  [onboardingTypes.demo]: 2,
  [onboardingTypes.migrate]: 3,
  [onboardingTypes.kickstart]: 2,
  [onboardingTypes.scratch]: 2,
  [onboardingTypes.default]: 0
};

export function getSteps({
  isVerificationNeeded,
  onboardingType,
  activeStep
}: {
  isVerificationNeeded: boolean;
  onboardingType: string;
  activeStep: number;
}): Array<RoutableWizardStep> {
  const numberOfSteps =
    (isVerificationNeeded ? 1 : 0) +
    onboardingTypeNumberOfStepsMap[onboardingType];
  return [...Array(numberOfSteps).keys()].map(number => ({
    number: number + 1,
    status:
      number + 1 < activeStep
        ? "completed"
        : number + 1 === activeStep
          ? "active"
          : "default"
  }));
}

const FirstOnboardingRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const { load: reloadProvisioningProjectCheck } = useProvisioningPuller();
  const { organizationId } = useParams<{ organizationId }>();
  const {
    setupStepperValues,
    verificationType,
    activeStep,
    isCreateProjectLoading,
    handleUpdateSetupStepperValues,
    handleGoForward
  } = useSetupStepperContext();
  const isOnSetupPage = location.pathname.includes("/setup/");
  const organizations = useAppSelector(organizationsSelector);
  const shouldShowCancelButton = cancellableSubdirectories.some(subdirectory =>
    location.pathname.includes(subdirectory)
  );
  const selectedOrganization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const urlOnboardingType = searchParams.get("onboardingType");
  const shouldShowStepper = stepperSubdirectories.some(subdirectory =>
    location.pathname.includes(subdirectory)
  );

  useEffect(() => {
    const preLoadingContainer = document.getElementById("pre-loading");

    if (preLoadingContainer?.parentNode) {
      preLoadingContainer.parentNode.removeChild(preLoadingContainer);
    }
  }, []);

  const isOrganizationLoading = useAppSelector(organizationLoadingSelector);
  const [hasOrganizationsStartedLoading, setHasOrganizationsStartedLoading] =
    useState(false);

  useEffect(() => {
    if (!hasOrganizationsStartedLoading && isOrganizationLoading)
      setHasOrganizationsStartedLoading(true);
  }, [hasOrganizationsStartedLoading, isOrganizationLoading]);

  useEffect(() => {
    if (
      hasOrganizationsStartedLoading &&
      !isOrganizationLoading &&
      Object.keys(organizations)?.length === 0
    ) {
      navigate("/");
    }
    if (urlOnboardingType) {
      handleUpdateSetupStepperValues({
        ...setupStepperValues,
        onboardingType: urlOnboardingType
      });
      handleGoForward();
      searchParams.delete("onboardingType");
      setSearchParams(searchParams);
    } else if (
      setupStepperValues.onboardingType === onboardingTypes.default &&
      redirectSubdirectories.some(subdirectory =>
        location.pathname.endsWith(subdirectory)
      )
    ) {
      navigate(
        `${
          setupStepperValues.organization.name
            ? `/${setupStepperValues.organization.name}`
            : ".."
        }/create-project`
      );
    }
  }, [
    setupStepperValues,
    location.pathname,
    navigate,
    organizations,
    isOrganizationLoading,
    urlOnboardingType,
    handleUpdateSetupStepperValues,
    handleGoForward,
    searchParams,
    setSearchParams,
    hasOrganizationsStartedLoading
  ]);

  if (
    verificationType === verificationTypes.default ||
    verificationType === verificationTypes.idle ||
    isCreateProjectLoading
  ) {
    return (
      <S.LoadingContainer>
        <Loading />
      </S.LoadingContainer>
    );
  }

  const fallbackContainer = document.getElementById("fallback");
  if (fallbackContainer) {
    fallbackContainer.parentNode?.removeChild(fallbackContainer);
  }

  if (!selectedOrganization && !isOrganizationLoading) {
    return <Navigate to="/" />;
  }

  return (
    <S.PageContainer>
      <Trial organizationId={organizationId} />
      <S.IconCloseButtonWrapper>
        <S.LogoWrapper>
          <Logo isFullLogo />
        </S.LogoWrapper>
        {shouldShowCancelButton && (
          <S.CloseButton
            onClick={() => {
              reloadProvisioningProjectCheck();
              navigate("/");
            }}
          >
            {intl.formatMessage({ id: isOnSetupPage ? "skip" : "cancel" })}
          </S.CloseButton>
        )}
      </S.IconCloseButtonWrapper>
      {shouldShowStepper && (
        <S.StepperWrapper>
          <WizardStepperWrapper
            steps={getSteps({
              isVerificationNeeded: verificationType !== verificationTypes.none,
              onboardingType: setupStepperValues.onboardingType,
              activeStep
            })}
            hasLabel={false}
          />
        </S.StepperWrapper>
      )}
      <Routes>
        <Route path="details" element={<Details />} />
        <Route path="verify" element={<Verify />} />
        <Route path="setup/:flowType/:projectId" element={<Setup />} />
        <Route path="repository" element={<Repository />} />
        <Route path="" element={<OnboardingType />} />
        <Route
          path="*"
          element={<Navigate to={`/${organizationId}/create-project`} />}
        />
      </Routes>
    </S.PageContainer>
  );
};

const WrappedRoutes = () => {
  return (
    <SetupStepperContextProvider>
      <FirstOnboardingRoutes />
    </SetupStepperContextProvider>
  );
};

export default WrappedRoutes;
